import { useMemo, useState } from "react";
import { Badge, Button, Icon, useToast } from "@agentsaas/ui";
import { useErrorText, useT } from "../app/i18n";
import { useServices } from "../app/services";
import { browserPushEnv, PushController, type PushEnv, type PushState } from "./push";

const TONE: Record<PushState, "neutral" | "success" | "warning" | "danger"> = { unsupported: "neutral", denied: "danger", off: "neutral", on: "success" };

/** 设置页：本设备的推送开关（Web Push 订阅 → deviceRegister） */
export function PushDeviceCard({ env }: { env?: PushEnv }) {
  const { t, locale } = useT();
  const { api, mock } = useServices();
  const toast = useToast();
  const errText = useErrorText();
  const ctl = useMemo(() => new PushController(api, env ?? browserPushEnv(), !!mock), [api, env, mock]);
  const [state, setState] = useState<PushState>(() => ctl.state());
  const [busy, setBusy] = useState(false);
  const run = async (fn: () => Promise<PushState>) => {
    setBusy(true);
    try {
      const next = await fn();
      setState(next);
      if (next === "on") toast.show({ message: t("pwa.push.enabled"), tone: "success", duration: 2000 });
    } catch (e) {
      toast.show({ message: errText(e), tone: "danger" });
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="push-card" data-testid="push-card" data-state={state}>
      <Icon name={state === "on" ? "bell" : "bellOff"} />
      <div className="grow">
        <div className="row-inline">
          <strong>{t("pwa.push.title")}</strong>
          <Badge tone={TONE[state]}>{t(`pwa.push.state.${state}`)}</Badge>
        </div>
        <div className="small muted">{t(`pwa.push.hint.${state}`)}</div>
      </div>
      {state === "off" ? (
        <Button variant="primary" size="sm" loading={busy} onClick={() => void run(() => ctl.enable(locale))} data-testid="push-enable">
          {t("pwa.push.enable")}
        </Button>
      ) : state === "on" ? (
        <Button size="sm" loading={busy} onClick={() => void run(() => ctl.disable())} data-testid="push-disable">
          {t("pwa.push.disable")}
        </Button>
      ) : null}
    </div>
  );
}
